const DISMISS_DISTANCE = 110;
const DISMISS_VELOCITY = 0.55;
const CLOSE_DURATION = 180;
const OVERLAY_BLUR_PX = 12;

function prefersReducedMotion() {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

function getOverlay(el) {
  return el.closest(".modal-backdrop-animated, .mobile-modal-overlay");
}

function setOverlayProgress(el, progress) {
  const overlay = getOverlay(el);
  if (!overlay) return;

  const amount = Math.min(1, Math.max(0, progress));
  const blur = Math.max(0, OVERLAY_BLUR_PX * (1 - amount));
  overlay.style.opacity = String(Math.max(0.18, 1 - amount * 0.72));
  overlay.style.backdropFilter = `blur(${blur}px)`;
  overlay.style.webkitBackdropFilter = `blur(${blur}px)`;
}

function resetOverlay(el) {
  const overlay = getOverlay(el);
  if (!overlay) return;

  overlay.style.transition =
    "opacity 180ms ease, backdrop-filter 180ms ease, -webkit-backdrop-filter 180ms ease";
  overlay.style.opacity = "1";
  overlay.style.backdropFilter = `blur(${OVERLAY_BLUR_PX}px)`;
  overlay.style.webkitBackdropFilter = `blur(${OVERLAY_BLUR_PX}px)`;
  window.setTimeout(() => {
    overlay.style.removeProperty("transition");
    overlay.style.removeProperty("opacity");
    overlay.style.removeProperty("backdrop-filter");
    overlay.style.removeProperty("-webkit-backdrop-filter");
  }, CLOSE_DURATION);
}

function resetSheet(el) {
  el.style.transition = "transform 180ms cubic-bezier(0.22, 1, 0.36, 1)";
  el.style.transform = "translateY(0)";
  el.style.opacity = "1";
  resetOverlay(el);
  window.setTimeout(() => {
    el.style.removeProperty("transition");
    el.style.removeProperty("transform");
    el.style.removeProperty("opacity");
  }, CLOSE_DURATION);
}

function dismissSheet(el, close) {
  if (el.__bottomSheetClosing) return;
  el.__bottomSheetClosing = true;

  if (prefersReducedMotion()) {
    close?.();
    return;
  }

  el.style.transition = `transform ${CLOSE_DURATION}ms cubic-bezier(0.4, 0, 1, 1)`;
  el.style.transform =
    window.innerWidth < 640 ? "translateY(105%)" : "translateY(8px) scale(0.98)";
  const overlay = getOverlay(el);
  if (overlay) {
    overlay.style.transition =
      "opacity 120ms ease 60ms, backdrop-filter 120ms ease 60ms, -webkit-backdrop-filter 120ms ease 60ms";
    overlay.style.opacity = "0";
    overlay.style.backdropFilter = "blur(0px)";
    overlay.style.webkitBackdropFilter = "blur(0px)";
  }
  window.setTimeout(() => close?.(), CLOSE_DURATION);
}

export function closeBottomSheet(close) {
  if (typeof document === "undefined") {
    close?.();
    return;
  }

  const sheets = document.querySelectorAll(
    ".mobile-bottom-sheet, .mobile-lightbox-content",
  );
  const sheet = sheets[sheets.length - 1];
  if (!sheet || prefersReducedMotion()) {
    close?.();
    return;
  }
  if (sheet.__bottomSheetClosing) {
    close?.();
    return;
  }
  dismissSheet(sheet, close);
}

export const vBottomSheet = {
  mounted(el, binding) {
    let startY = 0;
    let lastY = 0;
    let startTime = 0;
    let dragging = false;

    const onTouchStart = (event) => {
      if (window.innerWidth >= 640 || event.touches.length !== 1) return;
      if (el.scrollTop > 0) return;

      startY = event.touches[0].clientY;
      lastY = startY;
      startTime = performance.now();
      dragging = true;
      el.style.transition = "none";
      const overlay = getOverlay(el);
      if (overlay) overlay.style.transition = "none";
    };

    const onTouchMove = (event) => {
      if (!dragging) return;
      const currentY = event.touches[0].clientY;
      const distance = currentY - startY;
      lastY = currentY;

      if (distance <= 0) {
        el.style.transform = `translateY(${distance * 0.12}px)`;
        setOverlayProgress(el, 0);
        return;
      }

      event.preventDefault();
      el.style.transform = `translateY(${distance}px)`;
      el.style.opacity = String(Math.max(0.72, 1 - distance / 900));
      setOverlayProgress(el, distance / 260);
    };

    const onTouchEnd = () => {
      if (!dragging) return;
      dragging = false;

      const distance = Math.max(0, lastY - startY);
      const elapsed = Math.max(1, performance.now() - startTime);
      const velocity = distance / elapsed;

      if (distance >= DISMISS_DISTANCE || velocity >= DISMISS_VELOCITY) {
        dismissSheet(el, binding.value);
      } else {
        resetSheet(el);
      }
    };

    el.__bottomSheetHandlers = { onTouchStart, onTouchMove, onTouchEnd };
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });
  },

  updated(el, binding) {
    el.__bottomSheetClose = binding.value;
  },

  unmounted(el) {
    const handlers = el.__bottomSheetHandlers;
    if (!handlers) return;
    el.removeEventListener("touchstart", handlers.onTouchStart);
    el.removeEventListener("touchmove", handlers.onTouchMove);
    el.removeEventListener("touchend", handlers.onTouchEnd);
    el.removeEventListener("touchcancel", handlers.onTouchEnd);
    delete el.__bottomSheetHandlers;
    delete el.__bottomSheetClosing;
  },
};
